import React, { useEffect, useState } from 'react';
import { Alert, Button, Image, Layout, message, Typography } from 'antd';
import { ArrowLeftOutlined, CheckCircleTwoTone } from '@ant-design/icons'; 
import { getString } from '@nackle/intl-tools';
import { fallbackImage } from '../../const';
import { fetchCourse, fetchFile, patchAssignment, STATUS } from '../../services/CourseService/CourseService';
import WidgetCourseList from '../WidgetCourseList/WidgetCourseList';
import { trackScorm, openWindow } from '../../scormRuntime/ScormTracker';
import './styles.less';

const WidgetCourse = ( { courseOptions, course } ) => {
    const [ messageApi, contextHolder ] = message.useMessage();
    const [ courseData, setCourseData ] = useState( course );
    const [ coursesEnabled, setCoursesEnabled ] = useState( true );
    const [ courseWindow, setCourseWindow ] = useState();
    const [ reloadingCourse, setReloadingCourse ] = useState( false );

    const handleBackClick = () => {
        if ( courseOptions.referrer ) {
            location.href = courseOptions.referrer;
        } else {
            if ( window.history.length >= 0 ) {
                window.history.back();
            }
        }
    };
    
    const reloadCourse = async () => {
        setReloadingCourse( true );

        const result = await fetchCourse( 
            courseOptions.token, 
            courseOptions.courseId,
            courseOptions.locale
        );       
            
        if ( result.success ) { 
            setCourseData( result.course ); 
        }  

        setReloadingCourse( false );
        setCoursesEnabled( true );
    };

    /**************************************************
     * SCORM window handling, closes the window when 
     * the user navs away from this site.
     **************************************************/
    
    const handleCourseStartCallback = ( learningObject ) => {
        if ( learningObject.format === 'scorm' ) {
            setCoursesEnabled( false );

            const studentName = courseOptions.learnerFirstName && courseOptions.learnerFirstName ? 
                `${ courseOptions.learnerLastName }, ${ courseOptions.learnerFirstName }` : '';
            const studentId = courseOptions.learnerId ? courseOptions.learnerId : '';
 
            const url =  getScormURL( learningObject );
        
            trackScorm( course, learningObject, studentName, studentId, courseOptions.token );       
            setCourseWindow( openWindow( url, handleCloseCourseCallback ) );
        } else {
            setCoursesEnabled( false );

            if ( learningObject.content.mimetype === 'text/uri-list' ) {
                openLearningObjectFile( learningObject, learningObject.content.url );
                setCoursesEnabled( true );
            } else {
                // Fetch file url and open or download file
                fetchFile( courseOptions.token, learningObject.content.lxpFileId )
                    .then( ( response ) => {
                        if ( response.success  ) {
                            openLearningObjectFile( learningObject, response.fileURL );
                        } else { 
                            messageApi.open( {
                                type: 'warning',
                                content: 'A error occurred opening this file. Please try again later.',
                            } );

                            setCoursesEnabled( true );
                        }
                    }
                );
            }
        }
    };

    const getScormURL = ( lessonObject ) => {
        let url = lessonObject.content.url;

        /***********************************
         * Test Courses
         * 
         * Uncomment one of these lines to
         * test a course locally.
         * 
         * Also, you can change 
         * START_COURSE_AS_NEW to true
         * so all courses start as if they
         * have never been run.
         ***********************************/
            
        // Chamelean scorm 1.2 courses
        //url = 'http://localhost:3126/test-bourne-course/';    
        //url = 'http://localhost:3126/test-camelean-course/'; 
        //url = 'http://localhost:3126/chameleon4short/';                  
            
        // Chamelean scorm 2004 courses
        //url = 'http://localhost:3126/jdd-lizard-test-2004v4/index.html'; 
            
        // Rise scorm 1.2 courses
        //url = 'http://localhost:3126/single-vision-rise/scormdriver/indexAPI.html';
        //url = 'http://localhost:3126/rx-rise/scormdriver/indexAPI.html';
        //url = 'http://localhost:3126/rise-hygine/scormdriver/indexAPI.html';    
            
        const localeQuery = `?language=${ courseOptions.locale }`;
        return `${ url }${ localeQuery }`;
    };

    const handleCloseCourseCallback = () => {
        reloadCourse();
    };

    /**************************************************
     * SCORM window handling, closes the window when 
     * the user navs away from this site.
     **************************************************/
        
    const closeCourseWindow = () => {
        if ( courseWindow ) {
            courseWindow.close();
        }
    };
    
    useEffect( () => {
        window.addEventListener( 'beforeunload', closeCourseWindow );
        
        return () => {
            window.removeEventListener( 'beforeunload', closeCourseWindow );
        };
    }, [ closeCourseWindow ] );

    /**************************************************
     * Opens a non scorm file or url in a new browser
     * window and sets it's status to COMPLETE
     **************************************************/
    
    const openLearningObjectFile = ( learningObject, url ) => {
        if ( learningObject.status === STATUS.COMPLETED ) {
            window.open( url, '_blank' );
            setCoursesEnabled( true );
            return;
        }

        window.open( url, '_blank' );

        const inputAssigned = {
            input: { 
                learningObjectID: learningObject.id,
                courseID: learningObject.courseId,
                status: STATUS.ASSIGNED
            }
        };

        const inputCompleted = {
            input: { 
                learningObjectID: learningObject.id,
                courseID: learningObject.courseId,
                status: STATUS.COMPLETED
            }
        };
            
        patchAssignment( courseOptions.token, inputAssigned )
            .then( ( response ) => {
                if ( response.success === true  ) {
                    patchAssignment( courseOptions.token, inputCompleted )
                        .then( ( response ) => {
                            if ( response.success === true  ) {
                                reloadCourse();
                            }
                        } 
                    );
                } else {
                    setCoursesEnabled( true );
                }
            } 
        );
    };
    
    return (       
        <div className="widget-course">
            
            { /* Back button */ }
            <Button 
                className="back-button"
                type="link"
                icon={ <ArrowLeftOutlined /> } 
                onClick={ handleBackClick }
            >
                { getString( 'player.back.label' ) }
            </Button>

            <Layout>
                <Layout.Content>
                    { /* Hero image */ }    
                    <div className="hero-image">
                        <Image
                            preview={ false }
                            src={ courseData.heroImage }
                            fallback={ fallbackImage }
                        />
                    </div>

                    { /* Title */ }           
                    <Typography.Title level={ 1 }>
                        { courseData.name }
                    </Typography.Title>
                            
                    { /* Description */ }    
                    <Typography.Paragraph type="secondary">
                        <span dangerouslySetInnerHTML={ { __html: courseData.description } } />
                    </Typography.Paragraph>

                    { /* Course completed message */ }    
                    { courseData.status === STATUS.COMPLETED &&
                        <Alert 
                            message={ getString( 'course.completed' ) } 
                            icon={ <CheckCircleTwoTone twoToneColor="#52c41a"/> }
                            showIcon
                            type="info" 
                        />
                    }
 
                    { /* Course list */ }  
                    <WidgetCourseList
                        loading={ reloadingCourse }
                        enabled={ coursesEnabled }
                        course={ courseData }
                        courseStartCallback={ handleCourseStartCallback }
                    />

                    { contextHolder }
                </Layout.Content>
            </Layout>
        </div>
    );
};

export default WidgetCourse;
